import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, MailCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUS_STYLES = {
  pending: "text-amber-400 border-amber-400/30",
  contacted: "text-sky-400 border-sky-400/30",
  confirmed: "text-emerald-400 border-emerald-400/30",
  declined: "text-zinc-500 border-zinc-600",
};

export default function Account() {
  const [auth, setAuth] = useState(undefined);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setAuth(data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          setAuth(data);
        } catch {
          setAuth(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (auth) {
      api.get("/my-bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
    }
  }, [auth]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setSent(true);
      } else {
        const payload =
          mode === "login" ? { email: form.email, password: form.password } : form;
        const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", payload);
        setAuth(data);
        toast.success(mode === "login" ? "Welcome back." : "Account created — welcome to Sky Suite.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuth(false);
    setBookings([]);
  };

  if (auth === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian" data-testid="account-loading">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (!auth) {
    if (mode === "forgot") {
      return (
        <div className="flex min-h-screen items-center justify-center bg-obsidian px-6 py-16" data-testid="forgot-page">
          <div className="spotlight-card w-full max-w-md p-10">
            <div className="mb-8 flex justify-center"><Logo /></div>
            {sent ? (
              <div className="text-center" data-testid="forgot-sent-panel">
                <MailCheck size={40} className="mx-auto text-gold" />
                <h1 className="mt-5 font-serif text-2xl text-slate-100">Check Your Inbox</h1>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  If an account exists for {form.email}, a reset link is on its way. It expires in 1 hour.
                </p>
                <button onClick={() => { setMode("login"); setSent(false); }} data-testid="forgot-back-login-btn"
                  className="mt-8 rounded-full border border-gold/40 px-8 py-3 font-display text-xs font-semibold tracking-[0.25em] text-gold uppercase transition-colors hover:bg-gold/10">
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                <h1 className="text-center font-serif text-2xl text-slate-100">Reset Password</h1>
                <p className="text-center text-sm text-zinc-500">Enter your account email and we'll send a reset link.</p>
                <div>
                  <label className="lux-label" htmlFor="forgot-email">Email</label>
                  <input id="forgot-email" data-testid="forgot-email-input" type="email" required className="lux-input"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
                </div>
                {error && <p data-testid="forgot-error" className="text-sm text-red-400">{error}</p>}
                <button type="submit" data-testid="forgot-submit-btn" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase transition-all hover:bg-gold-bright disabled:opacity-60">
                  {busy && <Loader2 size={15} className="animate-spin" />}
                  Email Me a Reset Link
                </button>
                <button type="button" onClick={() => setMode("login")} data-testid="forgot-cancel-btn"
                  className="block w-full text-center text-xs text-zinc-500 transition-colors hover:text-gold">
                  ← Back to Sign In
                </button>
              </form>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-6 py-16" data-testid="account-page">
        <div className="spotlight-card w-full max-w-md p-10">
          <div className="mb-8 flex justify-center"><Logo /></div>
          <div className="mb-8 flex rounded-full border border-gold/20 p-1">
            {[["login", "Sign In"], ["register", "Create Account"]].map(([id, label]) => (
              <button
                key={id}
                data-testid={`account-${id}-tab`}
                onClick={() => { setMode(id); setError(""); }}
                className={`flex-1 rounded-full py-2.5 font-display text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  mode === id ? "bg-gold text-obsidian" : "text-zinc-400 hover:text-gold"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="account-form">
            {mode === "register" && (
              <div>
                <label className="lux-label" htmlFor="account-name">Full Name</label>
                <input id="account-name" data-testid="account-name-input" required className="lux-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jordan Carter" />
              </div>
            )}
            <div>
              <label className="lux-label" htmlFor="account-email">Email</label>
              <input id="account-email" data-testid="account-email-input" type="email" required className="lux-input"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="lux-label" htmlFor="account-password">Password</label>
                {mode === "login" && (
                  <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link"
                    className="mb-2 text-[11px] text-gold/70 transition-colors hover:text-gold">
                    Forgot password?
                  </button>
                )}
              </div>
              <input id="account-password" data-testid="account-password-input" type="password" required
                minLength={mode === "register" ? 8 : 1} className="lux-input"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={mode === "register" ? "8+ characters" : "••••••••"} />
            </div>
            {error && <p data-testid="account-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" data-testid="account-submit-btn" disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase transition-all hover:bg-gold-bright disabled:opacity-60">
              {busy && <Loader2 size={15} className="animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </button>
            <Link to="/" data-testid="account-back-home-link" className="block text-center text-xs text-zinc-500 transition-colors hover:text-gold">
              ← Back to site
            </Link>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="account-dashboard">
      <header className="border-b border-gold/15">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="account-home-link"><Logo /></Link>
          <button onClick={logout} data-testid="account-logout-btn"
            className="flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 font-display text-[11px] tracking-[0.2em] text-gold uppercase transition-colors hover:bg-gold/10">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-14">
        <p className="eyebrow">My Account</p>
        <h1 className="mt-3 font-serif text-3xl text-slate-100">
          Welcome, <span className="gold-gradient-text italic">{auth.name || auth.email}</span>
        </h1>
        <p className="mt-3 text-sm text-zinc-500">Track every booking request and its status below.</p>

        <div className="mt-10 space-y-5" data-testid="my-bookings-list">
          {bookings.length === 0 && (
            <div className="spotlight-card p-10 text-center" data-testid="my-bookings-empty">
              <p className="text-zinc-400">No bookings yet.</p>
              <Link to="/" className="mt-4 inline-block rounded-full bg-gold px-8 py-3 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase">
                Reserve Your Arrival
              </Link>
            </div>
          )}
          {bookings.map((b) => (
            <div key={b.booking_id} className="spotlight-card flex flex-wrap items-center justify-between gap-4 p-6" data-testid={`my-booking-${b.reference}`}>
              <div>
                <p className="font-mono text-xs tracking-[0.2em] text-gold">{b.reference}</p>
                <p className="mt-1 font-serif text-xl text-slate-100">{b.item_name}</p>
                <p className="mt-1 font-mono text-[11px] text-zinc-500">{b.start_date} → {b.end_date} · {b.guests} guests</p>
              </div>
              <div className="text-right">
                <p className="font-serif text-2xl text-slate-100">${b.total_price.toLocaleString()}</p>
                <span className={`mt-2 inline-block rounded-full border px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase ${STATUS_STYLES[b.status] ?? ""}`}>
                  {b.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
